import { CareerPath, Major, MAJOR_ATTRIBUTE_DISPLAY_LABELS } from '@backrs_ui/common-ui';
import type { MAJOR_ATTRIBUTE_OPTIONS } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

const STRINGS = appStrings.modules.majors;

/**
 * Filters career paths based on their relevance to a major.
 * Returns an array of CareerPath objects that match the major's careerPathRelevance.
 * If no relevant career paths are found, returns an empty array.
 *
 * @param major - The major object containing careerPathRelevance data
 * @param careerPaths - Array of all available career paths to match against
 * @returns Array of CareerPath objects that are relevant to the major
 */
export const getHighRelevanceCareerPathsForMajors = ({
  major,
  careerPaths,
}: {
  major: Major;
  careerPaths: CareerPath[];
}): CareerPath[] => {
  if (!major.careerPathRelevance || !careerPaths.length) {
    return [];
  }

  return (
    major.careerPathRelevance
      .map((relevance) => careerPaths.find((cp) => cp.id === relevance.careerPathId))
      .filter((cp): cp is CareerPath => cp !== undefined) || []
  );
};

/**
 * Categorizes major rigor score into one of 4 categories and returns the corresponding text for chart display
 * @param score - The rigor score from the API (typically 0-15)
 * @returns rigor label
 */
export const getMajorRigorText = (score: number): string => {
  const RIGOR_STRINGS = STRINGS.academicExperience.majorRigorText;

  if (score >= 15) {
    return RIGOR_STRINGS.veryRigorous;
  }

  if (score >= 10 && score <= 14) {
    return RIGOR_STRINGS.rigorous;
  }

  if (score >= 7 && score <= 9) {
    return RIGOR_STRINGS.moderate;
  }

  return RIGOR_STRINGS.notRigorous;
};

/**
 * Maps grad school necessity rationale text to chart percentage
 * @param rationale - The grad school necessity rationale text (Rarely, Sometimes, Frequently, Most of the Time)
 * @returns Percent (25, 50, 75, or 100) for ProgressCircleChart
 */
export const getGradSchoolNecessityPercent = (
  rationale: Major['gradSchoolNecessity'],
): 0 | 25 | 50 | 75 | 100 => {
  if (rationale === 'Most of the time') {
    return 100;
  }

  if (rationale === 'Frequently') {
    return 75;
  }

  if (rationale === 'Sometimes') {
    return 50;
  }

  // Default to "Rarely" for any other value
  return 25;
};

/**
 * Calculates underemployment rating for a major compared to average
 * @param major - The major object containing underEmployment data
 * @returns Object with rating text, underemployment percentage, icon color, and icon type
 */
export const getMajorUnderEmploymentRating = (
  major: Major,
): {
  rating: string;
  majorUnderEmployment: number;
  iconColor: 'Success/500' | 'Warning/500' | 'Error/500';
  icon: 'ThumbsUp' | 'ThumbsDown';
} => {
  const averageUnderEmployment = 52;
  const majorUnderEmployment = major.underEmployment * 100;

  // Determine rating and color based on underEmployment value (lower is better)
  const getRatingAndColor = (
    value: number,
  ): {
    rating: string;
    iconColor: 'Success/500' | 'Warning/500' | 'Error/500';
    icon: 'ThumbsUp' | 'ThumbsDown';
  } => {
    if (value < 30) {
      return { rating: 'Much better than most', iconColor: 'Success/500', icon: 'ThumbsUp' };
    }
    if (value < 50) {
      return { rating: 'Better than most', iconColor: 'Success/500', icon: 'ThumbsUp' };
    }
    if (value < averageUnderEmployment) {
      return { rating: 'Not Bad', iconColor: 'Success/500', icon: 'ThumbsUp' };
    }
    if (value < 60) {
      return { rating: 'Average', iconColor: 'Warning/500', icon: 'ThumbsUp' };
    }

    return { rating: 'Worse than most', iconColor: 'Error/500', icon: 'ThumbsDown' };
  };

  const { rating, iconColor, icon } = getRatingAndColor(majorUnderEmployment);

  return {
    rating,
    majorUnderEmployment,
    iconColor,
    icon,
  };
};

/**
 * Compares early career wages for a major against the average ($55,000)
 * @param major - The major object containing medianWageEarlyCareer data
 * @returns Comparison string describing how the major's early career wages compare to average
 */
export const getEarlyCareerComparison = (major: Major) => {
  const majorWage = major.medianWageEarlyCareer;
  const ratio = majorWage / AVERAGE_MEDIAN_EARLY_CAREER_WAGE;

  if (ratio > 1.15) return STRINGS.earnings.comparisons.earlyCareer.significantlyMore;
  if (ratio > 1.05) return STRINGS.earnings.comparisons.earlyCareer.more;
  if (ratio >= 0.95) return STRINGS.earnings.comparisons.earlyCareer.aboutSame;
  if (ratio >= 0.85) return STRINGS.earnings.comparisons.earlyCareer.slightlyLess;

  return STRINGS.earnings.comparisons.earlyCareer.less;
};

/**
 * Compares mid-career wages for a major against the average ($84,000)
 * @param major - The major object containing medianWageMidCareer data
 * @returns Comparison string describing how the major's mid-career wages compare to average
 */
export const getMidCareerComparison = (major: Major) => {
  const majorWage = major.medianWageMidCareer;
  const ratio = majorWage / AVERAGE_MEDIAN_MID_CAREER_WAGE;

  if (ratio > 1.15) return STRINGS.earnings.comparisons.midCareer.significantlyHigher;
  if (ratio > 1.05) return STRINGS.earnings.comparisons.midCareer.higher;
  if (ratio >= 0.95) return STRINGS.earnings.comparisons.midCareer.aboutEqual;
  if (ratio >= 0.85) return STRINGS.earnings.comparisons.midCareer.justBelow;

  return STRINGS.earnings.comparisons.midCareer.below;
};

/**
 * Calculates overall earnings rating for a major based on early and mid-career wages
 * @param major - The major object containing wage data
 * @returns Object with rating text, icon color, and icon type
 */
export const getMajorEarningsRating = (
  major: Major,
): {
  rating: string;
  iconColor: 'Success/500' | 'Warning/500' | 'Error/500';
  icon: 'ThumbsUp' | 'ThumbsDown';
} => {
  const earlyRatio = major.medianWageEarlyCareer / AVERAGE_MEDIAN_EARLY_CAREER_WAGE;
  const midRatio = major.medianWageMidCareer / AVERAGE_MEDIAN_MID_CAREER_WAGE;
  const combinedRatio = (earlyRatio + midRatio) / 2;

  switch (true) {
    case combinedRatio > 1.15:
      return {
        rating: STRINGS.earnings.rating.muchBetterThanMost,
        iconColor: 'Success/500',
        icon: 'ThumbsUp',
      };
    case combinedRatio > 1.05:
      return {
        rating: STRINGS.earnings.rating.betterThanMost,
        iconColor: 'Success/500',
        icon: 'ThumbsUp',
      };
    case combinedRatio >= 0.95:
      return { rating: STRINGS.earnings.rating.notBad, iconColor: 'Success/500', icon: 'ThumbsUp' };
    case combinedRatio >= 0.85:
      return {
        rating: STRINGS.earnings.rating.average,
        iconColor: 'Warning/500',
        icon: 'ThumbsUp',
      };
    default:
      return {
        rating: STRINGS.earnings.rating.worseThanMost,
        iconColor: 'Error/500',
        icon: 'ThumbsDown',
      };
  }
};

/**
 * Calculates positioning values for chart labels relative to the zero line
 * @param LABEL_HEIGHT - Height of the label element in pixels
 * @param CHART_HEIGHT - Total height of the chart in pixels
 * @returns Object with label height percent, zero line position (percent and px), and distance from bottom
 */
export const getChartPositioning = (LABEL_HEIGHT: number, CHART_HEIGHT: number) => {
  const labelHeightPercent = (LABEL_HEIGHT / 2 / CHART_HEIGHT) * 100;
  const zeroLinePosition = 100 - labelHeightPercent;
  const zeroLinePositionPx = (CHART_HEIGHT * zeroLinePosition) / 100;
  const distanceFromBottom = CHART_HEIGHT - zeroLinePositionPx;

  return {
    labelHeightPercent,
    zeroLinePosition,
    zeroLinePositionPx,
    distanceFromBottom,
  };
};

/**
 * Gets the display label for a major attribute value
 * @param attributeKey - The key of the major attribute (e.g., 'workEnvironment', 'skills')
 * @param fullText - The full text value from the API
 * @param isMobile - Whether to show shortened labels (mobile) or full text (desktop)
 * @returns The shortened display label for mobile, or full text for desktop
 */
export function getMajorAttributeDisplayLabel(
  attributeKey: keyof typeof MAJOR_ATTRIBUTE_OPTIONS,
  fullText: string,
  isMobile: boolean,
): string {
  if (!isMobile) {
    return fullText;
  }

  const mapping = MAJOR_ATTRIBUTE_DISPLAY_LABELS[attributeKey];

  return mapping[fullText] ?? fullText;
}

export const AVERAGE_MEDIAN_EARLY_CAREER_WAGE = 55000;
export const AVERAGE_MEDIAN_MID_CAREER_WAGE = 84000;
